import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { EntityUsageCard } from '../components/EntityUsageCard';
import { IS_ADMIN_SURFACE } from '../utils/surface';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Table } from '../components/ui/Table';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { PhoneInput } from '../components/ui/PhoneInput';
import { AddressAutocomplete } from '../components/ui/AddressAutocomplete';
import { apiGet, apiCreate, apiList, apiUpdate } from '../api/apiClient';
import { getCustomers, updateCustomer } from '../api/customers';
import { CustomerForm } from '../components/forms/CustomerForm';
import { PropertyForm } from '../components/forms/PropertyForm';
import { getProperties } from '../api/properties';
import { UsersList } from '../components/UsersList';
import { getDevices } from '../api/devices';
import { getUnits } from '../api/units';
import { STRIPE_PROXY_URL, calcDeviceCost } from '../config/billing';
import { CreateSubscriptionModal } from '../components/billing/CreateSubscriptionModal';
import { CreateInvoiceModal } from '../components/billing/CreateInvoiceModal';
import { EditStripeCustomerModal } from '../components/billing/EditStripeCustomerModal';
import { SubscriptionActions } from '../components/billing/SubscriptionActions';
import { SubscriptionDetailModal } from '../components/billing/SubscriptionDetailModal';
import { RatesTab } from '../components/billing/RatesTab';
import { FeatureToggleList } from '../components/billing/FeatureToggleList';
import { useAuth } from '../hooks/useAuth';
import { isSuperAdmin } from '../utils/roles';
import { refreshAllCounters } from '../utils/refreshCounters';

const statusColor = (s: string) => {
  switch (s) {
    case 'active': case 'paid': return 'green';
    case 'trialing': return 'blue';
    case 'past_due': case 'open': return 'yellow';
    case 'canceled': case 'incomplete_expired': case 'void': return 'red';
    default: return 'gray';
  }
};

const fmtDate = (ts: number) => ts ? new Date(ts * 1000).toLocaleDateString() : '\u2014';
const fmtAmount = (cents: number) => cents != null ? `$${(cents / 100).toFixed(2)}` : '\u2014';

/* ─── tiny section header with edit button ─── */
const SectionHeader: React.FC<{ title: string; count?: number; onEdit?: () => void }> = ({ title, count, onEdit }) => (
  <div className="p-4 border-b border-gray-100 flex items-center justify-between">
    <h3 className="font-semibold text-gray-900">{title}{count != null ? ` (${count})` : ''}</h3>
    {onEdit && <Button variant="secondary" size="sm" onClick={onEdit}>Edit</Button>}
  </div>
);

export const IntegratorDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || 'admin';

  /* ─── state ─── */
  const [integrator, setIntegrator] = useState<any>(null);
  const [customers, setCustomers] = useState<any[]>([]);
  const [properties, setProperties] = useState<any[]>([]);
  const [devices, setDevices] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailTab, setDetailTab] = useState<'overview' | 'portfolio' | 'users'>('overview');
  const [allSubs, setAllSubs] = useState<any[]>([]);
  const [allInvoices, setAllInvoices] = useState<any[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);

  /* modals */
  const [editInfoOpen, setEditInfoOpen] = useState(false);
  const [editBillingOpen, setEditBillingOpen] = useState(false);
  const [createSubOpen, setCreateSubOpen] = useState(false);
  const [createInvOpen, setCreateInvOpen] = useState(false);
  const [editStripeOpen, setEditStripeOpen] = useState(false);
  const [createCustOpen, setCreateCustOpen] = useState(false);
  const [assignCustOpen, setAssignCustOpen] = useState(false);
  const [unassignedCustomers, setUnassignedCustomers] = useState<any[]>([]);
  const [createPropertyOpen, setCreatePropertyOpen] = useState(false);
  const [assignPropertyOpen, setAssignPropertyOpen] = useState(false);
  const [unassignedProperties, setUnassignedProperties] = useState<any[]>([]);
  const [assignPropertyId, setAssignPropertyId] = useState('');
  const [selectedCustId, setSelectedCustId] = useState('');
  const [infoForm, setInfoForm] = useState<any>({});
  const [billingForm, setBillingForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [expandedSubId, setExpandedSubId] = useState<string | null>(null);
  const [detailSub, setDetailSub] = useState<any>(null);
  const [expandedInvId, setExpandedInvId] = useState<string | null>(null);
  const [sendEmailInvId, setSendEmailInvId] = useState<string | null>(null);
  const [sendEmailAddr, setSendEmailAddr] = useState('');

  /* ─── load data ─── */
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const [intData, custData, propData, devData, unitData] = await Promise.all([
          apiGet('integrators', id!),
          getCustomers().catch(() => []),
          getProperties().catch(() => []),
          getDevices().catch(() => []),
          getUnits().catch(() => []),
        ]);
        const int = {
          ...intData,
          company_name: intData.company_name || intData.company || intData.name || '',
          contact_name: intData.contact_name || intData.name || '',
          contact_email: intData.contact_email || intData.email || '',
          phone: intData.phone || '',
          address: intData.address || '',
          city: intData.city || '',
          state: intData.state || '',
          zip: intData.zip || '',
          status: intData.status || 'active',
          billing_contact_name: intData.billing_contact_name || '',
          billing_email: intData.billing_email || '',
          billing_address: intData.billing_address || '',
          billing_city: intData.billing_city || '',
          billing_state: intData.billing_state || '',
          billing_zip: intData.billing_zip || '',
          stripe_customer_id: intData.stripe_customer_id || '',
        };
        setIntegrator(int);

        const myCusts = (custData || []).filter((c: any) => String(c.integrator_id) === String(id) || c.integrator_uuid === id);
        setCustomers(myCusts);

        const custIds = new Set(myCusts.map((c: any) => c.id));
        const myProps = (propData || []).filter((p: any) => custIds.has(p.customer_id));
        setProperties(myProps);

        const propIds = new Set(myProps.map((p: any) => p.id));
        setDevices((devData || []).filter((d: any) => propIds.has(d.property_id)));
        setUnits((unitData || []).filter((u: any) => propIds.has(u.property_id)));

        // Stripe billing
        const stripeCusts = myCusts.filter((c: any) => c.stripe_customer_id);
        if (stripeCusts.length > 0) {
          setBillingLoading(true);
          const results = await Promise.all(stripeCusts.map(async (c: any) => {
            const [subs, invs] = await Promise.all([
              fetch(`${STRIPE_PROXY_URL}/subscriptions?customer=${c.stripe_customer_id}`).then(r => r.json()).catch(() => []),
              fetch(`${STRIPE_PROXY_URL}/invoices?customer=${c.stripe_customer_id}`).then(r => r.json()).catch(() => []),
            ]);
            const custName = c.company || c.company_name || c.name;
            return {
              subs: (Array.isArray(subs) ? subs : subs?.data || []).map((s: any) => ({ ...s, _customerName: custName, _stripeCustomerId: c.stripe_customer_id })),
              invs: (Array.isArray(invs) ? invs : invs?.data || []).map((i: any) => ({ ...i, _customerName: custName })),
            };
          }));
          setAllSubs(results.flatMap(r => r.subs));
          setAllInvoices(results.flatMap(r => r.invs));
          setBillingLoading(false);
        }
      } catch (e) {
        console.error('Failed to load integrator:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  /* ─── derived counts ─── */
  const deviceCountByProperty = useCallback((propId: number | string) => devices.filter(d => d.property_id === String(propId)).length, [devices]);
  const propCountByCustomer = useCallback((custId: number | string) => properties.filter(p => p.customer_id === String(custId)).length, [properties]);
  const deviceCountByCustomer = useCallback((custId: number | string) => {
    const pIds = new Set(properties.filter(p => p.customer_id === String(custId)).map(p => p.uuid || p.id));
    return devices.filter(d => pIds.has(d.property_id)).length;
  }, [properties, devices]);

  const monthlyTotal = allSubs.reduce((sum, s) => {
    if (s.status !== 'active' && s.status !== 'trialing') return sum;
    const item = s.items?.data?.[0];
    return sum + (item ? (item.price?.unit_amount || 0) * (item.quantity || 1) : 0);
  }, 0);

  const primaryStripeId = integrator?.stripe_customer_id || customers.find((c: any) => c.stripe_customer_id)?.stripe_customer_id || '';

  /* ─── save handlers ─── */
  const saveInfo = async () => {
    setSaving(true);
    try {
      const same = infoForm.same_billing_address;
      const billingAddress = same ? infoForm.address : infoForm.billing_address;
      const billingCity = same ? infoForm.city : infoForm.billing_city;
      const billingState = same ? infoForm.state : infoForm.billing_state;
      const billingZip = same ? infoForm.zip : infoForm.billing_zip;
      await apiUpdate('integrators', id!, {
        company_name: infoForm.company_name,
        contact_name: infoForm.contact_name,
        contact_email: infoForm.contact_email,
        phone: infoForm.phone,
        address: infoForm.address,
        city: infoForm.city,
        state: infoForm.state,
        zip: infoForm.zip,
        status: infoForm.status,
        billing_contact_name: infoForm.billing_contact_name,
        billing_email: infoForm.billing_email,
        billing_phone: infoForm.billing_phone,
        billing_address: billingAddress,
        billing_city: billingCity,
        billing_state: billingState,
        billing_zip: billingZip,
        billing_setting: infoForm.billing_setting,
        commission_rate: infoForm.billing_setting === 'dealer_billed' ? Number(infoForm.commission_rate || 0) : 0,
      });
      refreshAllCounters();
      // Sync Stripe customer to mirror this dealer's contact + billing info.
      const stripeId = integrator.stripe_customer_id || primaryStripeId;
      if (stripeId) {
        const stripeAddr = same ? infoForm.address : infoForm.billing_address;
        const stripeCity = same ? infoForm.city : infoForm.billing_city;
        const stripeState = same ? infoForm.state : infoForm.billing_state;
        const stripeZip = same ? infoForm.zip : infoForm.billing_zip;
        try {
          await fetch(`${STRIPE_PROXY_URL}/customers/${stripeId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: infoForm.company_name,
              email: infoForm.billing_email || infoForm.contact_email,
              phone: infoForm.billing_phone || infoForm.phone,
              address: {
                line1: stripeAddr,
                city: stripeCity,
                state: stripeState,
                postal_code: stripeZip,
              },
            }),
          });
        } catch { /* non-fatal */ }
      }
      setIntegrator((prev: any) => ({ ...prev, ...infoForm }));
      setEditInfoOpen(false);
    } finally { setSaving(false); }
  };

  const saveBilling = async () => {
    setSaving(true);
    try {
      await apiUpdate('integrators', id!, {
        billing_contact_name: billingForm.billing_contact_name,
        billing_email: billingForm.billing_email,
        stripe_customer_id: billingForm.stripe_customer_id,
      });
      refreshAllCounters();
      // Sync Stripe customer with billing info (fall back to dealer info)
      const stripeId = billingForm.stripe_customer_id || primaryStripeId;
      if (stripeId) {
        const billingAddr = billingForm.billing_address || integrator.address;
        const billingCity = billingForm.billing_city || integrator.city;
        const billingState = billingForm.billing_state || integrator.state;
        const billingZip = billingForm.billing_zip || integrator.zip;
        await fetch(`${STRIPE_PROXY_URL}/customers/${stripeId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: integrator.company_name,
            email: billingForm.billing_email || integrator.contact_email,
            phone: integrator.phone,
            address: {
              line1: billingAddr,
              city: billingCity,
              state: billingState,
              postal_code: billingZip,
            },
          }),
        }).catch(() => {});
      }
      setIntegrator((prev: any) => ({ ...prev, ...billingForm }));
      setEditBillingOpen(false);
    } finally { setSaving(false); }
  };

  /* ─── render ─── */
  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;
  if (!integrator) return <div className="text-center py-12 text-gray-400">Dealer not found</div>;

  return (
    <div className="shadow-detail-page dealer-detail-page">
      {/* Back link */}
      <button onClick={() => navigate('/integrators')} className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>Back to Dealers
      </button>
      <header className="prototype-entity-hero"><div><small>Intrlock app / dealer</small><h1>{integrator.company_name}</h1><p>Dealer profile, customer portfolio, properties, users, systems, and account configuration.</p></div><div><Badge status={integrator.status}>{integrator.status}</Badge><Button variant="secondary" onClick={() => { setInfoForm({ ...integrator }); setEditInfoOpen(true); }}>Edit dealer</Button></div></header>

      {/* Operations Quick View — live counters + usage scoped to this dealer */}
      <div className="mb-6">
        <EntityUsageCard entityType="dealer" entityId={(integrator as any)?.uuid || (integrator as any)?.id || id} />
      </div>

      {/* ══════════ Stats Grid ══════════ */}
      <div className="prototype-detail-metrics grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Customers</p>
          <p className="text-xl font-bold text-gray-900">{integrator.total_customers || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Properties</p>
          <p className="text-xl font-bold text-gray-900">{integrator.total_properties || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Units</p>
          <p className="text-xl font-bold text-gray-900">{integrator.total_units || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Devices</p>
          <p className="text-xl font-bold text-gray-900">{integrator.total_devices || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Tenants</p>
          <p className="text-xl font-bold text-gray-900">{integrator.total_tenants || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Guests</p>
          <p className="text-xl font-bold text-gray-900">{integrator.total_guests || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Phones</p>
          <p className="text-xl font-bold text-gray-900">{integrator.total_phones || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Systems</p>
          <p className="text-xl font-bold text-gray-900">{integrator.total_systems || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Intercoms</p>
          <p className="text-xl font-bold text-emerald-600">{(integrator as any).total_intercoms || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Mobile Apps</p>
          <p className="text-xl font-bold text-gray-900">{(integrator as any).total_active_activations || 0}</p>
        </div>
      </div>
      <nav className="person-tabs dealer-detail-tabs" aria-label="Dealer detail sections"><button className={detailTab === 'overview' ? 'active' : ''} onClick={() => setDetailTab('overview')}>Overview</button><button className={detailTab === 'portfolio' ? 'active' : ''} onClick={() => setDetailTab('portfolio')}>Customers & Properties <b>{customers.length + properties.length}</b></button><button className={detailTab === 'users' ? 'active' : ''} onClick={() => setDetailTab('users')}>Users</button></nav>

      {/* ══════════ Top row: Info + Billing side by side ══════════ */}
      <div className={`dealer-overview-layout grid grid-cols-1 gap-6 mb-6 ${detailTab === 'overview' ? '' : 'hidden'}`}>
        {/* Section 1: Integrator Info */}
        <Card padding={false}>
          <SectionHeader title="Dealer Info" onEdit={() => {
            const sameAddr =
              !integrator.billing_address ||
              ((integrator.billing_address || '') === (integrator.address || '') &&
               (integrator.billing_city || '') === (integrator.city || '') &&
               (integrator.billing_state || '') === (integrator.state || '') &&
               (integrator.billing_zip || '') === (integrator.zip || ''));
            setInfoForm({
              company_name: integrator.company_name,
              contact_name: integrator.contact_name,
              contact_email: integrator.contact_email,
              phone: integrator.phone,
              address: integrator.address,
              city: integrator.city,
              state: integrator.state,
              zip: integrator.zip,
              status: integrator.status,
              billing_contact_name: integrator.billing_contact_name || '',
              billing_email: integrator.billing_email || '',
              billing_phone: integrator.billing_phone || '',
              billing_address: integrator.billing_address || '',
              billing_city: integrator.billing_city || '',
              billing_state: integrator.billing_state || '',
              billing_zip: integrator.billing_zip || '',
              same_billing_address: sameAddr,
              billing_setting: integrator.billing_setting || 'dealer_billed',
              commission_rate: integrator.commission_rate || 0,
            });
            setEditInfoOpen(true);
          }} />
          <div className="p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Company</span><span className="font-medium text-gray-900">{integrator.company_name}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Contact Name</span><span className="text-gray-900">{integrator.contact_name || '\u2014'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Email</span><span className="text-gray-900">{integrator.contact_email || '\u2014'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Phone</span><span className="text-gray-900">{integrator.phone || '\u2014'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Address</span><span className="text-gray-900">{[integrator.address, integrator.city, integrator.state, integrator.zip].filter(Boolean).join(', ') || '\u2014'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Status</span><Badge status={integrator.status}>{integrator.status}</Badge></div>
            <hr className="my-2" />
            <div className="flex justify-between"><span className="text-gray-500">Customers</span><span className="font-semibold">{customers.length}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Properties</span><span className="font-semibold">{properties.length}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Units</span><span className="font-semibold">{units.length}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Devices</span><span className="font-semibold">{devices.length}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Phone Numbers</span><span className="font-semibold">{properties.filter((p: any) => p.telnyx_number).length}</span></div>
          </div>
        </Card>

        {/* Billing Details \u2014 moved to admin.intrlock.io */}
        {false && (
        <Card padding={false}>
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Billing Details</h3>
          </div>
          <div className="p-4 space-y-2 text-sm">
          </div>
        </Card>
        )}
      </div>

      {/* ══════════ Users ══════════ */}
      <div className={`dealer-users-panel mt-6 mb-6 ${detailTab === 'users' ? '' : 'hidden'}`}>
        <Card>
          <div className="p-4">
            <UsersList filterType="integrator" filterId={id!} title="Users" />
          </div>
        </Card>
      </div>

      {/* ══════════ Customers Table ══════════ */}
      <div className={`dealer-portfolio-panels space-y-6 ${detailTab === 'portfolio' ? '' : 'hidden'}`}>
        <Card padding={false}>
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Customers ({customers.length})</h3>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={async () => { const all = await getCustomers().catch(() => []); setUnassignedCustomers((all || []).filter((c: any) => !c.integrator_id || c.integrator_id === 'NONE' || c.integrator_id === '0' || c.integrator_id === 0 || c.dealer_id === 'NONE' || (!c.dealer_id && !c.integrator_id))); setSelectedCustId(''); setAssignCustOpen(true); }}>Assign Existing</Button>
              <Button size="sm" onClick={() => setCreateCustOpen(true)}>Add Customer</Button>
            </div>
          </div>
          <Table columns={[
            { key: 'company', header: 'Company', sticky: 'primary' as const, render: (c: any) => <Link onClick={(e) => e.stopPropagation()} to={`/customers/${c.uuid || c.id}`} className="font-medium text-brand-700 hover:underline">{c.company || c.company_name || c.name}</Link> },
            { key: 'name', header: 'Contact', render: (c: any) => c.contact_name || c.name || '\u2014' },
            { key: 'properties', header: 'Properties', render: (c: any) => propCountByCustomer(c.uuid || c.id) },
            { key: 'units', header: 'Units', render: (c: any) => { const custProps = properties.filter((p: any) => String(p.customer_id) === String(c.uuid || c.id)); return units.filter((u: any) => custProps.some((p: any) => String(p.uuid || p.id) === String(u.property_id))).length; } },
            { key: 'devices', header: 'Devices', render: (c: any) => deviceCountByCustomer(c.uuid || c.id) },
            { key: 'phones', header: 'Phones', render: (c: any) => properties.filter((p: any) => String(p.customer_id) === String(c.uuid || c.id) && p.telnyx_number).length },
            { key: 'status', header: 'Status', render: (c: any) => <Badge status={c.status}>{c.status}</Badge> },
            { key: '_actions', header: '', sticky: 'actions' as const, render: (c: any) => <Link onClick={(e) => e.stopPropagation()} to={`/customers/${c.uuid || c.id}`} className="row-open">Open <span aria-hidden="true">→</span></Link> },
          ]} data={customers} onRowClick={(c) => navigate(`/customers/${c.uuid || c.id}`)} emptyMessage="No customers yet" />
        </Card>

        {/* ══════════ Properties Table (enhanced) ══════════ */}
        <Card padding={false}>
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Properties ({properties.length})</h3>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={async () => { const all = await apiList('properties'); setUnassignedProperties((all || []).filter((p: any) => !p.customer_id || p.customer_id === '0' || p.customer_id === 0 || p.customer_id === 'NONE')); setAssignPropertyId(''); setAssignPropertyOpen(true); }}>Assign Existing</Button>
              <Button size="sm" onClick={() => setCreatePropertyOpen(true)}>Add Property</Button>
            </div>
          </div>
          <Table columns={[
            { key: 'name', header: 'Name', sticky: 'primary' as const, render: (p: any) => <Link onClick={(e) => e.stopPropagation()} to={`/properties/${p.uuid || p.id}`} className="font-medium text-brand-700 hover:underline">{p.name}</Link> },
            { key: 'address', header: 'Address', render: (p: any) => [p.address, p.city, p.state].filter(Boolean).join(', ') || '\u2014' },
            { key: 'units', header: 'Units', render: (p: any) => units.filter((u: any) => String(u.property_id) === String(p.uuid || p.id)).length },
            { key: 'devices', header: 'Devices', render: (p: any) => deviceCountByProperty(p.uuid || p.id) },
            { key: 'phones', header: 'Phones', render: (p: any) => p.telnyx_number ? 1 : 0 },
            { key: 'status', header: 'Status', render: (p: any) => <Badge status={p.status}>{p.status}</Badge> },
            { key: '_actions', header: '', sticky: 'actions' as const, render: (p: any) => <Link onClick={(e) => e.stopPropagation()} to={`/properties/${p.uuid || p.id}`} className="row-open">Open <span aria-hidden="true">→</span></Link> },
          ]} data={properties} onRowClick={(p) => navigate(`/properties/${p.uuid || p.id}`)} emptyMessage="No properties yet" />
        </Card>

        {/* ══════════ Subscriptions + Invoices — moved to admin.intrlock.io ══════════ */}
        {false && (<>
        <Card padding={false}>
          <SectionHeader title="Subscriptions" count={allSubs.length} />
          {billingLoading ? (
            <div className="p-4 text-gray-400 text-sm">Loading billing data...</div>
          ) : allSubs.length === 0 ? (
            <div className="p-4 text-gray-400 text-sm">No subscriptions across customers</div>
          ) : (
            <div className="divide-y divide-gray-100">
              <div className="grid grid-cols-8 gap-2 px-4 py-2 text-xs font-medium text-gray-500 uppercase bg-gray-50">
                <span>Customer</span><span>Status</span><span>Amount</span><span>Start Date</span><span>Period</span><span>Paused</span><span>Payment</span><span>Actions</span>
              </div>
              {allSubs.map((s: any) => {
                const items = s.items?.data || [];
                const totalAmount = items.reduce((sum: number, item: any) => sum + (item.price?.unit_amount || 0) * (item.quantity || 1), 0);
                return (
                  <div
                    key={s.id}
                    className="grid grid-cols-8 gap-2 px-4 py-3 text-sm items-center cursor-pointer hover:bg-gray-50"
                    onClick={() => setDetailSub(s)}
                  >
                    <span className="font-medium truncate">{s._customerName}</span>
                    <span><Badge color={statusColor(s.status)}>{s.status}</Badge></span>
                    <span>{fmtAmount(totalAmount)}</span>
                    <span className="text-gray-600">{fmtDate(s.start_date || s.created)}</span>
                    <span className="text-gray-600 text-xs">{fmtDate(s.current_period_start)} – {fmtDate(s.current_period_end)}</span>
                    <span>{s.pause_collection ? <Badge color="yellow">Yes</Badge> : '—'}</span>
                    <span className="text-xs text-gray-500">{s.default_payment_method ? '•••• ' + (s.default_payment_method.card?.last4 || s.default_payment_method.slice?.(-4) || '') : '—'}</span>
                    <span onClick={e => e.stopPropagation()}><SubscriptionActions subscription={s} onUpdated={() => window.location.reload()} /></span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* ══════════ Invoices ══════════ */}
        <Card padding={false}>
          <SectionHeader title="Invoices" count={allInvoices.length} />
          {billingLoading ? (
            <div className="p-4 text-gray-400 text-sm">Loading billing data...</div>
          ) : allInvoices.length === 0 ? (
            <div className="p-4 text-gray-400 text-sm">No invoices across customers</div>
          ) : (
            <div className="divide-y divide-gray-100">
              <div className="grid grid-cols-7 gap-2 px-4 py-2 text-xs font-medium text-gray-500 uppercase bg-gray-50">
                <span>Customer</span><span>Invoice</span><span>Date</span><span>Amount</span><span>Status</span><span>PDF</span><span>Send</span>
              </div>
              {allInvoices.map((inv: any) => {
                const lines = inv.lines?.data || [];
                const isExpanded = expandedInvId === inv.id;
                return (
                  <div key={inv.id}>
                    <div className="grid grid-cols-7 gap-2 px-4 py-3 text-sm items-center cursor-pointer hover:bg-gray-50" onClick={() => setExpandedInvId(isExpanded ? null : inv.id)}>
                      <span className="font-medium truncate">{inv._customerName}</span>
                      <span className="font-medium">{inv.number || '—'}</span>
                      <span className="text-gray-600">{fmtDate(inv.created)}</span>
                      <span>{fmtAmount(inv.amount_due)}</span>
                      <span><Badge color={statusColor(inv.status)}>{inv.status}</Badge></span>
                      <span onClick={e => e.stopPropagation()}>
                        {inv.invoice_pdf ? <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline text-xs">Download</a> : '—'}
                      </span>
                      <span onClick={e => e.stopPropagation()}>
                        <button className="text-xs text-brand-600 hover:underline" onClick={() => { setSendEmailInvId(inv.id); setSendEmailAddr(inv.customer_email || integrator.billing_email || integrator.contact_email || ''); }}>Email</button>
                      </span>
                    </div>
                    {isExpanded && lines.length > 0 && (
                      <div className="bg-gray-50 px-8 py-3 border-t border-gray-100">
                        <p className="text-xs font-medium text-gray-500 mb-2">Line Items</p>
                        <div className="space-y-1">
                          {lines.map((line: any, idx: number) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-gray-700">{line.description || line.price?.nickname || line.price?.product?.name || `Item ${idx + 1}`}</span>
                              <span className="font-medium">{fmtAmount(line.amount)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between text-sm font-semibold border-t border-gray-200 pt-1 mt-1">
                            <span>Total</span>
                            <span>{fmtAmount(inv.amount_due)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
        </>)}
      </div>

      {/* ══════════ Edit Integrator Info Modal ══════════ */}
      <Modal open={editInfoOpen} onClose={() => setEditInfoOpen(false)} title="Edit Dealer Info" size="lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <Input label="Company Name" value={infoForm.company_name || ''} onChange={e => setInfoForm((f: any) => ({ ...f, company_name: e.target.value }))} />
          <Input label="Contact Name" value={infoForm.contact_name || ''} onChange={e => setInfoForm((f: any) => ({ ...f, contact_name: e.target.value }))} />
          <Input label="Email" type="email" value={infoForm.contact_email || ''} onChange={e => setInfoForm((f: any) => ({ ...f, contact_email: e.target.value }))} />
          <PhoneInput label="Phone" value={infoForm.phone || ''} onChange={(v) => setInfoForm((f: any) => ({ ...f, phone: v }))} />
          <Input label="Status" value={infoForm.status || ''} onChange={e => setInfoForm((f: any) => ({ ...f, status: e.target.value }))} />
        </div>

        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-4 mb-2 pb-1 border-b border-gray-200">Mailing Address</h4>
        <AddressAutocomplete
          label="Street Address"
          value={infoForm.address || ''}
          onChange={(v) => setInfoForm((f: any) => ({ ...f, address: v }))}
          onSelect={(p) => setInfoForm((f: any) => ({
            ...f,
            address: p.street || f.address,
            city: p.city || f.city,
            state: p.state || f.state,
            zip: p.zip || f.zip,
          }))}
        />
        <div className="grid grid-cols-3 gap-3">
          <Input label="City" value={infoForm.city || ''} onChange={e => setInfoForm((f: any) => ({ ...f, city: e.target.value }))} />
          <Input label="State" value={infoForm.state || ''} onChange={e => setInfoForm((f: any) => ({ ...f, state: e.target.value }))} />
          <Input label="Zip" value={infoForm.zip || ''} onChange={e => setInfoForm((f: any) => ({ ...f, zip: e.target.value }))} />
        </div>

        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-4 mb-2 pb-1 border-b border-gray-200">Billing Contact</h4>
        <Input label="Billing Contact Name" value={infoForm.billing_contact_name || ''} onChange={e => setInfoForm((f: any) => ({ ...f, billing_contact_name: e.target.value }))} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <Input label="Billing Email" type="email" value={infoForm.billing_email || ''} onChange={e => setInfoForm((f: any) => ({ ...f, billing_email: e.target.value }))} />
          <PhoneInput label="Billing Phone" value={infoForm.billing_phone || ''} onChange={(v) => setInfoForm((f: any) => ({ ...f, billing_phone: v }))} />
        </div>
        <label className="flex items-center gap-2 mb-3 mt-1 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!infoForm.same_billing_address}
            onChange={(e) => setInfoForm((f: any) => ({ ...f, same_billing_address: e.target.checked }))}
            className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          <span className="text-sm text-gray-700">Billing address is the same as the mailing address</span>
        </label>
        {!infoForm.same_billing_address && (
          <>
            <AddressAutocomplete
              label="Billing Street Address"
              value={infoForm.billing_address || ''}
              onChange={(v) => setInfoForm((f: any) => ({ ...f, billing_address: v }))}
              onSelect={(p) => setInfoForm((f: any) => ({
                ...f,
                billing_address: p.street || f.billing_address,
                billing_city: p.city || f.billing_city,
                billing_state: p.state || f.billing_state,
                billing_zip: p.zip || f.billing_zip,
              }))}
            />
            <div className="grid grid-cols-3 gap-3">
              <Input label="Billing City" value={infoForm.billing_city || ''} onChange={e => setInfoForm((f: any) => ({ ...f, billing_city: e.target.value }))} />
              <Input label="Billing State" value={infoForm.billing_state || ''} onChange={e => setInfoForm((f: any) => ({ ...f, billing_state: e.target.value }))} />
              <Input label="Billing Zip" value={infoForm.billing_zip || ''} onChange={e => setInfoForm((f: any) => ({ ...f, billing_zip: e.target.value }))} />
            </div>
          </>
        )}

        {IS_ADMIN_SURFACE && (
          <>
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-4 mb-2 pb-1 border-b border-gray-200">Default Billing Settings</h4>
            <p className="text-xs text-gray-500 mb-2">Applied to new customers under this dealer. Dealers can override per customer at account setup.</p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Billing Model</label>
              <select
                value={infoForm.billing_setting || 'dealer_billed'}
                onChange={e => setInfoForm((f: any) => ({ ...f, billing_setting: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="dealer_billed">Intrlock bills dealer (less rebate)</option>
                <option value="customer_billed">Intrlock bills customer directly</option>
                <option value="property_billed">Intrlock bills property directly</option>
              </select>
            </div>
            {infoForm.billing_setting === 'dealer_billed' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Dealer Rebate %</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={infoForm.commission_rate || 0}
                    onChange={e => setInfoForm((f: any) => ({ ...f, commission_rate: e.target.value }))}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-500">% returned to dealer</span>
                </div>
              </div>
            )}
          </>
        )}
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="secondary" onClick={() => setEditInfoOpen(false)}>Cancel</Button>
          <Button onClick={saveInfo} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </div>
      </Modal>

      {/* ══════════ Create Subscription Modal ══════════ */}
      <SubscriptionDetailModal
        open={!!detailSub}
        onClose={() => setDetailSub(null)}
        subscriptionId={detailSub?.id || null}
        customerName={detailSub?._customerName}
      />

      <CreateSubscriptionModal
        open={createSubOpen}
        onClose={() => setCreateSubOpen(false)}
        defaultCustomerId={primaryStripeId}
        customers={customers.map(c => ({ id: c.id, company_name: c.company || c.company_name || c.name, stripe_customer_id: c.stripe_customer_id }))}
        onCreated={() => window.location.reload()}
      />

      {/* ══════════ Create Invoice Modal ══════════ */}
      <CreateInvoiceModal
        open={createInvOpen}
        onClose={() => setCreateInvOpen(false)}
        defaultCustomerId={primaryStripeId}
        customers={customers.map(c => ({ id: c.id, company_name: c.company || c.company_name || c.name, stripe_customer_id: c.stripe_customer_id }))}
        onCreated={() => window.location.reload()}
      />


      {/* ══════════ Create Customer Modal ══════════ */}
      <Modal open={createCustOpen} onClose={() => setCreateCustOpen(false)} title="Create Customer" size="lg">
        <CustomerForm
          defaultIntegratorId={id as any}
          onSubmit={async () => { setCreateCustOpen(false); window.location.reload(); }}
          onCancel={() => setCreateCustOpen(false)}
        />
      </Modal>

      {/* ══════════ Assign Customer Modal ══════════ */}
      <Modal open={assignCustOpen} onClose={() => setAssignCustOpen(false)} title="Assign Existing Customer">
        {unassignedCustomers.length === 0 ? (
          <p className="text-sm text-gray-500">No unassigned customers available.</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Customer</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={selectedCustId}
                onChange={e => setSelectedCustId(e.target.value)}
              >
                <option value="">— Select —</option>
                {unassignedCustomers.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.company_name || c.company || c.name} ({c.billing_email || c.contact_email || 'no email'})</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setAssignCustOpen(false)}>Cancel</Button>
              <Button disabled={!selectedCustId} onClick={async () => { await updateCustomer(selectedCustId, { integrator_id: id }); setAssignCustOpen(false); window.location.reload(); }}>Assign</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ══════════ Add Property Modal ══════════ */}
      <Modal open={createPropertyOpen} onClose={() => setCreatePropertyOpen(false)} title="Add Property">
        <PropertyForm
          onSubmit={async (data: any) => {
            await apiCreate('properties', { ...data });
            refreshAllCounters();
            setCreatePropertyOpen(false);
            window.location.reload();
          }}
          onCancel={() => setCreatePropertyOpen(false)}
        />
      </Modal>

      {/* ══════════ Assign Existing Property Modal ══════════ */}
      <Modal open={assignPropertyOpen} onClose={() => setAssignPropertyOpen(false)} title="Assign Existing Property">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Select an unassigned property to link to one of this dealer's customers.</p>
          {unassignedProperties.length === 0 ? (
            <p className="text-sm text-gray-400">No unassigned properties available.</p>
          ) : (
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={assignPropertyId}
              onChange={(e) => setAssignPropertyId(e.target.value)}
            >
              <option value="">Select a property...</option>
              {unassignedProperties.map((p: any) => (
                <option key={p.uuid || p.id} value={p.uuid || p.id}>{p.name} — {p.address || 'No address'}</option>
              ))}
            </select>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setAssignPropertyOpen(false)}>Cancel</Button>
            <Button size="sm" disabled={!assignPropertyId} onClick={async () => {
              if (!assignPropertyId) return;
              // Link property to first customer of this dealer if possible
              const firstCust = customers[0];
              const custId = firstCust ? (firstCust.uuid || firstCust.id) : undefined;
              await apiUpdate('properties', assignPropertyId, custId ? { customer_id: custId } : {});
              refreshAllCounters();
              setAssignPropertyOpen(false);
              setAssignPropertyId('');
              window.location.reload();
            }}>Assign</Button>
          </div>
        </div>
      </Modal>

      {/* Billing Rates + Feature Billing live only on admin.intrlock.io. */}
      {isSuperAdmin(role)
        && (typeof window !== 'undefined' && window.location.hostname.startsWith('admin.')) && (
        <>
          <div className="mt-6">
            <RatesTab
              scopeType="dealer"
              scopeId={id!}
              entityName={integrator.company_name}
              dealerId={id!}
            />
          </div>
          <div className="mt-6">
            <FeatureToggleList entityType="dealer" entityId={id!} />
          </div>
        </>
      )}

      {/* ══════════ Send Invoice Email Modal ══════════ */}
      <Modal open={!!sendEmailInvId} onClose={() => setSendEmailInvId(null)} title="Send Invoice">
        <Input label="Email Address" type="email" value={sendEmailAddr} onChange={e => setSendEmailAddr(e.target.value)} />
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="secondary" onClick={() => setSendEmailInvId(null)}>Cancel</Button>
          <Button onClick={async () => {
            if (!sendEmailInvId || !sendEmailAddr) return;
            try {
              await fetch(`${STRIPE_PROXY_URL}/invoices/${sendEmailInvId}/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: sendEmailAddr }),
              });
              setSendEmailInvId(null);
            } catch (e) { console.error('Failed to send invoice:', e); }
          }}>Send</Button>
        </div>
      </Modal>

      {/* ══════════ Edit Stripe Customer Modal ══════════ */}
      {primaryStripeId && (
        <EditStripeCustomerModal
          open={editStripeOpen}
          onClose={() => setEditStripeOpen(false)}
          stripeCustomerId={primaryStripeId}
          onUpdated={() => window.location.reload()}
        />
      )}
    </div>
  );
};
